// 図鑑（観察ノート）モーダル。客 5 種を一覧表示する。
//
// 未発見はシルエット + 「???」、発見済みは名前・フレーバー・累計訪問数を出す。
// DOM 直組みの軽量実装。表示のたびに最新の Dex を受け取って描画し直す。

import { CUSTOMER_CATALOG, getCustomer } from '../sim/customers';
import type { Dex } from '../sim/state';
import { customerThumbnail } from '../render/thumbnail';
import { revealedHints, relationsOf, informants } from '../sim/discovery';

export interface DexModal {
  /** 最新の Dex でモーダルを開く。 */
  open(dex: Dex): void;
  /** 閉じる。 */
  close(): void;
  /** 開いているか。 */
  readonly isOpen: boolean;
}

export interface DexModalOptions {
  /** モーダルのルート要素（#dexModal）。 */
  modalEl: HTMLElement;
  /** 行を流し込むリスト要素（#dexList）。 */
  listEl: HTMLElement;
}

/**
 * 相関グラフのストリップ（未発見客の行に出す）。その客の関係先を絵文字アイコンで並べ、
 * 発見済みは点灯・未発見はシルエット風に薄く。関係が深い（重みが大きい）順に並べる。
 * これで「誰と仲がいいか＝誰を見つければヒントが進むか」が視覚的に分かる。
 */
function relationStrip(customerId: string, dex: Dex): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'dex-rel';
  const rels = relationsOf(customerId).slice().sort((a, b) => b.weight - a.weight);
  for (const r of rels) {
    const cust = getCustomer(r.id);
    if (!cust) continue;
    const discovered = !!dex[r.id]?.discovered;
    const chip = document.createElement('span');
    chip.className = 'dex-rel-ic' + (discovered ? ' on' : '');
    chip.textContent = discovered ? cust.icon : '◦';
    chip.title = discovered ? cust.name : '未発見の関係者';
    wrap.appendChild(chip);
  }
  return wrap;
}

export function createDexModal(opts: DexModalOptions): DexModal {
  const { modalEl, listEl } = opts;

  function render(dex: Dex): void {
    listEl.innerHTML = '';
    for (const c of CUSTOMER_CATALOG) {
      const entry = dex[c.id] ?? { discovered: false, count: 0, firstAtMs: null };
      const row = document.createElement('div');
      row.className = 'dex-row' + (entry.discovered ? '' : ' undiscovered');

      // 顔は客の実メッシュをオフスクリーンレンダしたサムネ（未発見は CSS でシルエット化）。
      const face = document.createElement('div');
      face.className = 'face';
      const img = document.createElement('img');
      img.alt = c.name;
      img.draggable = false;
      img.src = customerThumbnail(c.id);
      face.appendChild(img);

      const info = document.createElement('div');
      info.className = 'info';
      const nm = document.createElement('div');
      nm.className = 'nm';
      nm.textContent = entry.discovered ? c.name : '???';
      info.appendChild(nm);

      if (entry.discovered) {
        const fl = document.createElement('div');
        fl.className = 'fl';
        fl.textContent = c.flavor;
        info.appendChild(fl);
        const ct = document.createElement('div');
        ct.className = 'ct';
        ct.textContent = `これまでの来店: ${entry.count} 回`;
        info.appendChild(ct);
      } else {
        // 未発見: 相関グラフ（関係先を発見済みなら点灯）＋発見済みの仲間からの段階ヒント。
        info.appendChild(relationStrip(c.id, dex));
        const hints = revealedHints(c.id, dex);
        if (hints.length === 0) {
          const fl = document.createElement('div');
          fl.className = 'fl';
          fl.textContent = 'まだ手がかりがない。常連と出会えば、噂を聞けるかも。';
          info.appendChild(fl);
        } else {
          const who = informants(c.id, dex)
            .map((id) => getCustomer(id)?.name)
            .filter(Boolean);
          for (let hi = 0; hi < hints.length; hi++) {
            const hint = document.createElement('div');
            hint.className = 'dex-hint';
            hint.textContent = `💬 ${hints[hi]}`;
            info.appendChild(hint);
          }
          if (who.length > 0) {
            const src = document.createElement('div');
            src.className = 'dex-hint-src';
            src.textContent = `（${who.join('・')}が教えてくれた）`;
            info.appendChild(src);
          }
        }
      }

      row.append(face, info);
      listEl.appendChild(row);
    }
  }

  function open(dex: Dex): void {
    render(dex);
    modalEl.classList.add('open');
  }
  function close(): void {
    modalEl.classList.remove('open');
  }

  // 背景クリック / とじるボタンで閉じる
  modalEl.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t === modalEl || t.hasAttribute('data-close')) close();
  });

  return {
    open,
    close,
    get isOpen() {
      return modalEl.classList.contains('open');
    },
  };
}
